import axios from "axios";
import { convertCoordinateToCesiumCrs } from "./cesium-common";

/**
 * 地形情報を取得するAIサーバへのAPIリクエスト
 * @param {string} pointId 点群ID
 * @param {string} designId 設計ID
 * @param {string} userId ユーザーID
 * @returns {Promise<Object|null>} 地形情報データまたはnull
 */
export const getTerrainInfo = async (pointId, designId, userId) => {
	try {
		const response = await fetch(
			`${import.meta.env.VITE_API_ROADAI}/ai-road/?user_id=${userId}&point_id=${pointId}&design_id=${designId}&timestamp=${new Date().getTime()}`,
			{
				method: "GET",
			},
		);
		if (response.ok) {
			const data = await response.json();
			return data;
		} else {
			return null;
		}
	} catch (error) {
		console.log(error);
		return null;
	} finally {
	}
};

/**
 * 地形情報からaiParameter.inputTextFieldの値を自動設定します。
 */
export function setInputTextFieldByTerrain(
	store,
	terrainInfo,
	startpoint,
	endpoint,
	inputTextField,
) {
	const updatedInputTextField = {
		...inputTextField,
		xValueMin: String(
			Math.max(terrainInfo.terrain_x_value_min, terrainInfo.design_x_value_min),
		),
		xValueMax: String(
			Math.min(terrainInfo.terrain_x_value_max, terrainInfo.design_x_value_max),
		),
		yValueMin: String(
			Math.max(terrainInfo.terrain_y_value_min, terrainInfo.design_y_value_min),
		),
		yValueMax: String(
			Math.min(terrainInfo.terrain_y_value_max, terrainInfo.design_y_value_max),
		),
		xyInterval: "10",
		zValueMin: String(
			Math.floor(
				Math.min(
					Math.max(
						terrainInfo.terrain_height_min,
						terrainInfo.design_height_min,
					),
					startpoint[2],
					endpoint[2],
				),
			) - 1,
		),
		zValueMax: String(
			Math.ceil(
				Math.max(
					Math.min(
						terrainInfo.terrain_height_max,
						terrainInfo.design_height_max,
					),
					startpoint[2],
					endpoint[2],
				),
			) + 1,
		),
		zInterval: "5",
		allowMaxGradient: "14",
	};

	// ここで設定した値を使ってsearchExpansionRadiusを計算
	const xyInterval = updatedInputTextField.xyInterval;
	const zInterval = updatedInputTextField.zInterval;
	const allowMaxGradient = updatedInputTextField.allowMaxGradient;

	updatedInputTextField.searchExpansionRadius = Math.ceil(
		cal_suggest_radius(xyInterval, zInterval, allowMaxGradient),
	);

	// aiParameter.inputTextFieldを更新
	store.dispatch(
		"addRoadTerrainByAi/update_ai_parameter_input_text_field",
		updatedInputTextField,
	);

	// 始点・終点・terrainInfoも必要ならdispatchで更新
	store.dispatch("addRoadTerrainByAi/update_ai_parameter", {
		startpointXval: String(startpoint[0]),
		startpointYval: String(startpoint[1]),
		startpointZval: String(startpoint[2]),
		endpointXval: String(endpoint[0]),
		endpointYval: String(endpoint[1]),
		endpointZval: String(endpoint[2]),
		terrainInfo: terrainInfo,
	});
}

/**
 * 推奨探索半径を計算します。
 *
 * @param {number|string} xygrid_interval XY方向のグリッド間隔
 * @param {number|string} zgrid_interval Z方向のグリッド間隔
 * @param {number|string} max_gradient 最大勾配
 * @returns {string} 推奨探索半径（小数第3位までの文字列）
 */
export function cal_suggest_radius(
	xygrid_interval,
	zgrid_interval,
	max_gradient,
) {
	if (xygrid_interval === "" || zgrid_interval === "" || max_gradient === "") {
		return "1";
	}
	const xMin = (100 * zgrid_interval) / max_gradient;
	let x = 0;
	for (let i = 0; i < 10000; i++) {
		x = xygrid_interval * i;
		if (x > xMin) break;
	}
	const radius = Math.sqrt(x ** 2 + zgrid_interval ** 2);
	return radius.toFixed(3);
}

// TODO(D3D-774): 旧 ai-road 実行系。新方式（prototypeRoadAIUtils.runPrototypeRoadAI）へ移行済みで、チャット導線からは未使用。将来削除。
export const setRoadJsonAI = async (
	store,
	pointCloudAssetId,
	pointId,
	designId,
	userId,
) => {
	// getterメソッドでaiParameterを取得
	const aiParameter = store.getters["addRoadTerrainByAi/aiParameter"];

	const data = {
		userId: userId,
		pointId: pointId,
		designId: designId,
		inputTextField: aiParameter.inputTextField,
		terrainInfo: {
			sliderValueCut: aiParameter.sliderValueCut,
			sliderValueFill: aiParameter.sliderValueFill,
			sliderValueOverFill: aiParameter.sliderValueOverFill,
			sliderValueOverCut: aiParameter.sliderValueOverCut,
			sliderValueCfDiff: aiParameter.sliderValueCfDiff,
			sliderValueRoute: aiParameter.sliderValueRoute,
		},
		startpointXval: aiParameter.startpointXval,
		startpointYval: aiParameter.startpointYval,
		startpointZval: aiParameter.startpointZval,
		endpointXval: aiParameter.endpointXval,
		endpointYval: aiParameter.endpointYval,
		endpointZval: aiParameter.endpointZval,
	};
	try {
		// 仮設道路AI計算APIを実行
		if (!(aiParameter.startpointXval && aiParameter.endpointXval)) {
			store.dispatch("addRoadTerrainByAi/add_chat_message", {
				role: "assistant",
				text: "始点と終点が設定されていません。「始点と終点を設定するモードに切り替えて」と入力してください。",
			});
			store.dispatch("updateIsLoading", false);
			return;
		}
		const res = await axios.post(
			`${import.meta.env.VITE_API_ROADAI}/ai-road/roadAi/?timestamp=${new Date().getTime()}`,
			data,
			{
				headers: { "Content-Type": "application/json" },
				withCredentials: false, // trueにするとcorsでエラーになるため明示的にfalseにする
			},
		);
		if (res.status == 200) {
			const body = JSON.parse(res.data);
			const taskId = body.taskId;
			console.log(body);
			if (taskId) {
				// レスポンス内にtaskIdが存在する場合はポーリング開始
				return await startPolling(taskId, userId, pointCloudAssetId);
			}
		}
	} catch (error) {
		console.error(error.message);
		throw error;
	}
};

/**
 * 計算ステータス確認APIのポーリングを開始する
 * @param {*} taskId
 */
const startPolling = (taskId, userId, pointCloudAssetId) => {
	return new Promise((resolve, reject) => {
		let isComplete = false;
		const poll = async () => {
			try {
				isComplete = await checkCalculationStatus(
					taskId,
					userId,
					pointCloudAssetId,
				);
				if (isComplete) {
					clearInterval(timeId);
					resolve(isComplete);
				}
			} catch (e) {
				clearInterval(timeId);
				reject(e);
			}
		};
		poll();
		const timeId = setInterval(poll, 3000);
	});
};

export const executeChatbot = async (store) => {
	// getterメソッドでaiParameterを取得
	const chatContext = store.getters["addRoadTerrainByAi/postChatContext"];

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BASE}/obj/chatbot/`,
			chatContext,
		);
		return response.data;
	} catch (error) {
		console.error("Error in executeChatbot:", error.message);
		throw error;
	}
};

/**
 * 計算ステータス確認APIを実行する
 * @param {*} taskId
 * @param {*} userId
 * @returns roadAiData 仮設道路AIの計算結果
 */
const checkCalculationStatus = async (taskId, userId, pointCloudAssetId) => {
	let isComplete = false;
	let roadPoints = null;
	const res = await axios.get(
		`${import.meta.env.VITE_API_ROADAI}/ai-road/taskResult/?user_id=${userId}&task_id=${taskId}&timestamp=${new Date().getTime()}`,
		{
			headers: { "Content-Type": "application/json" },
			withCredentials: false, // corsでエラーになるため明示的にfalseにする
		},
	);
	// 正常応答の場合
	if (res.status == 200) {
		// 非同期処理が終了しているか確認。未終了の場合再実行
		let jsonStr = res.data;
		if (typeof jsonStr === "string") {
			jsonStr = jsonStr.replace(/'/g, '"');
		}
		const body = JSON.parse(jsonStr);

		if (body.ready === "True") {
			isComplete = true;
			if (
				body.state === "FAILURE" ||
				(body.state === "SUCCESS" && !body.data)
			) {
				throw new Error("AI計算に失敗しました");
			} else if (body.data?.error) {
				// AIサーバが返した明示的なエラー（点群/設計データ未登録など）
				throw new Error(body.data.error);
			} else {
				roadPoints = await convertCoordinates(body.data.points);
				// 仮設道路を作成する
				await window["viewer"].createRoadFromPointsWithAI(
					body.data.points, // 変換前の座標情報
					roadPoints, // 変換後の座標情報
					(id) => {
						const event = new CustomEvent("openPropertyDialog", {
							detail: { id: id, type: 0, isAI: true },
						});
						window.dispatchEvent(event);
					},
					pointCloudAssetId,
				);
			}
		}
	}
	return isComplete;
};

/**
 * 指定された座標配列を3つずつ分割し、各点をCesium座標系に変換して返します。
 *
 * @param {Array<number>} pointVal - 変換対象の座標配列（[x1, y1, z1, x2, y2, z2, ...]）
 * @returns {Promise<Array<number>>} 変換後の座標配列
 */
const convertCoordinates = async (pointVal) => {
	if (!Array.isArray(pointVal) || pointVal.length % 3 !== 0) {
		throw new Error("pointValは3の倍数の長さの配列である必要があります");
	}

	/**
	 * 配列を指定した数ごとに分割します。
	 * @param {Array} array - 分割対象の配列
	 * @param {number} number - 分割単位
	 * @returns {Array<Array>} 分割後の配列
	 */
	const sliceByNumber = (array, number) => {
		const length = Math.ceil(array.length / number);
		return Array.from({ length }, (_, i) =>
			array.slice(i * number, (i + 1) * number),
		);
	};

	const pointArray = sliceByNumber(pointVal, 3);
	const convertedArray = [];

	for (const point of pointArray) {
		if (point.length !== 3) continue; // 不正な点はスキップ
		const convert_p = await convertCoordinateToCesiumCrs({
			x: point[0],
			y: point[1],
			z: point[2],
		});
		convertedArray.push(convert_p.x, convert_p.y, convert_p.z);
	}

	return convertedArray;
};

/**
 * パラメータ情報の更新
 *
 * @param {Object} sliderValue
 * @returns {void}
 */
export function setSliderInfo(store, sliderValue) {
	if (!sliderValue) {
		return;
	}
	store.dispatch("addRoadTerrainByAi/update_ai_parameter", sliderValue);
}

/**
 * 勾配情報の更新
 *
 * @param {number} gradient
 * @returns
 */
export function setAllowMaxGradient(store, gradient) {
	if (!gradient) return;
	store.dispatch("addRoadTerrainByAi/update_ai_parameter_input_text_field", {
		allowMaxGradient: gradient,
	});
}

/**
 * 推奨探索半径の更新
 *
 * @param {number} gradient
 * @returns
 */
export function setSearchExpansionRadius(store, gradient) {
	if (!gradient) return;
	const aiParameter = store.getters["addRoadTerrainByAi/aiParameter"];
	const xyInterval = aiParameter.inputTextField.xyInterval;
	const zInterval = aiParameter.inputTextField.zInterval;
	const radius = Math.ceil(cal_suggest_radius(xyInterval, zInterval, gradient));
	store.dispatch("addRoadTerrainByAi/update_ai_parameter_input_text_field", {
		searchExpansionRadius: radius,
	});
}

/**
 * 始点終点の座標を取得するgetter
 * @returns
 */
export function getStartEndPoint(store) {
	const aiParameter = store.getters["addRoadTerrainByAi/aiParameter"];
	return {
		startpointXval: aiParameter.startpointXval,
		startpointYval: aiParameter.startpointYval,
		startpointZval: aiParameter.startpointZval,
		endpointXval: aiParameter.endpointXval,
		endpointYval: aiParameter.endpointYval,
		endpointZval: aiParameter.endpointZval,
	};
}

/**
 * AIパラメータのデフォルト値をリセットする
 */
export function resetDefaultValue(store) {
	store.dispatch("addRoadTerrainByAi/reset_ai_parameter");
}

/**
 * 地形情報計算APIを実行し、計算タスクのポーリングを開始します。
 *
 * @param {string|number} pointId 点群ID
 * @param {string|number} designId 設計ID
 * @param {string|number} userId ユーザーID
 * @returns {Promise<string|undefined>} タスクID（taskId）またはundefined
 */
export async function postPreprocess(pointId, designId, userId) {
	const checkStatus = async (taskId) => {
		try {
			const res = await axios.get(
				`${import.meta.env.VITE_API_ROADAI}/ai-road/taskResult/?user_id=${userId}&task_id=${taskId}&timestamp=${new Date().getTime()}`,
				{
					headers: { "Content-Type": "application/json" },
					withCredentials: false, // corsでエラーになるため明示的にfalseにする
				},
			);
			const body = JSON.parse(res.data);
			// 正常応答の場合
			if (res.status == 200) {
				// 非同期処理が終了しているか確認。未終了の場合再実行
				if (body.ready === "True") {
					if (
						body.state === "FAILURE" ||
						(body.state === "SUCCESS" && !body.data)
					) {
						//return true;
					}
					if (body.data?.error) {
						// AIサーバが返した明示的なエラー（点群/設計データ未登録など）
						throw new Error(body.data.error);
					}
					return true;
				}
			}
			return false;
		} catch (error) {
			throw error;
		}
	};

	const data = {
		pointId,
		designId,
		userId,
		terrainItems: [],
	};

	try {
		const res = await axios.post(
			`${import.meta.env.VITE_API_ROADAI}/ai-road/`,
			data,
			{ withCredentials: false }, // corsでエラーになるため明示的にfalseにする
		);
		if (res.status == 200) {
			const body = JSON.parse(res.data);
			const taskId = body.taskId;
			if (taskId) {
				// ポーリング処理: checkStatusがtrueを返すまで繰り返す
				return new Promise((resolve, reject) => {
					const poll = async () => {
						try {
							const isComplete = await checkStatus(taskId);
							if (isComplete) {
								clearInterval(timeId);
								resolve(taskId);
							}
						} catch (err) {
							clearInterval(timeId);
							reject(err);
						}
					};
					const timeId = setInterval(poll, 3000);
					poll();
				});
			}
		}
	} catch (error) {
		throw error;
	}
}
